import '@ghq-abi/design-system/css';
import '@ghq-abi/design-system-v2/css';

import * as React from 'react';
import { useRouter } from 'next/router';
import { Session } from 'next-auth';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import { DesignSystemProvider } from '@ghq-abi/design-system';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';

import { ErrorBoundary } from '~/app/components';
import { globalStyles } from '~/app/styles/global';
import { AppTemplate } from '~/app/templates/App';
import { HubTemplate } from '~/app/templates/Hub';
import { CustomAppProps } from '~/app/types';
import { AppTemplate as EIBAppTemplate } from '~/eib/templates/App';
import { AppAbility } from '~/shared/auth/permissions';
import { Flags } from '~/shared/contexts';
import { queryClientConfig, useDatadog } from '~/shared/lib';
import { AppProvider } from '~/shared/providers/AppProvider';
import { UserProfile } from '~/shared/types/profile';
import { normalizeLanguage } from '~/shared/utils/string';

import './globals.css';

globalStyles();

function AppContent({
  Component,
  pageProps,
  session,
  abilityRules,
  flags,
  profile
}: {
  Component: CustomAppProps['Component'];
  pageProps: CustomAppProps['pageProps'];
  session: Session | undefined;
  abilityRules: AppAbility['_indexedRules'] | undefined;
  flags: Partial<Flags> | undefined;
  profile?: UserProfile | null;
}) {
  const router = useRouter();
  const isHubRoute = router.pathname === '/home';
  const isEIBRoute = router.pathname.startsWith('/eib');
  const variant = isEIBRoute ? 'eib' : 'app';

  const AppTemplateComponent = isHubRoute
    ? HubTemplate
    : isEIBRoute
    ? EIBAppTemplate
    : AppTemplate;

  if (Component.unauth) {
    return <Component {...pageProps} />;
  }

  return (
    <AppProvider
      session={session}
      abilityRules={abilityRules}
      flags={flags}
      initialProfile={profile}
      variant={variant}
      AppTemplate={AppTemplateComponent}>
      <Hydrate state={pageProps.dehydratedState}>
        <Component {...pageProps} />
      </Hydrate>
    </AppProvider>
  );
}

export default function App({
  Component,
  pageProps: { session, abilityRules, flags, profile, ...pageProps }
}: CustomAppProps) {
  // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
  const [queryClient] = React.useState(
    () => new QueryClient(queryClientConfig)
  );

  const locale = normalizeLanguage(session?.user?.language);
  useDatadog(locale, session);

  return (
    <ErrorBoundary>
      <AuthClientProvider session={session}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <DesignSystemProvider>
              <AppContent
                Component={Component}
                pageProps={pageProps}
                session={session}
                abilityRules={abilityRules}
                flags={flags}
                profile={profile}
              />
            </DesignSystemProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthClientProvider>
    </ErrorBoundary>
  );
}
